import type { ConfigurationOperation } from '@festina/configuration-general-types/configuration'
import { STANDARD_BRANCH } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useEffect, useState } from 'react'

import { useMetaModelContext } from '../context/metaModelContext'
import { useNavigationParams } from '../system/useNavigationParams'
import { useGeneralBlocks } from '../uiModels/api/metaModel/useGeneralBlocks'
import {
  type TransferProps,
  useTransferOperation,
} from '../uiModels/transfer/useTransferOperation'
import { useIsPersonaEditor } from '../uiModels/useIsPersonaEditor'
import { useWorkspaceManagerContext } from '../workspace/workspaceManagerContext'
import { useBaseBranchWorkspaces } from './useBaseBranchWorkspaces'

type BoundTransferProps<T extends ConfigurationOperation> = Omit<
  TransferProps<T>,
  'sourceBlock' | 'destinationBlock' | 'layerTopology'
>

export const useTransferFromRouterLocation = () => {
  const { branch, block, server } = useNavigationParams()

  const ancestorsForViewBlock = useGeneralBlocks(block)
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false)
  const [selectedDestinationBlock, setSelectedDestinationBlock] = useState<
    Optional<string>
  >(ancestorsForViewBlock.at(0))

  const { layerTopology } = useMetaModelContext()
  const isEditor = useIsPersonaEditor()
  const branchWorkspaces = useBaseBranchWorkspaces({
    name: branch ?? '',
    server,
  })
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const { onTransfer: rawOnTransfer } = useTransferOperation()

  useEffect(() => {
    const updateTo = ancestorsForViewBlock.at(0)
    if (updateTo !== selectedDestinationBlock) {
      setSelectedDestinationBlock(updateTo)
    }
  }, [ancestorsForViewBlock, block, selectedDestinationBlock])

  const isSingleOrNoWorkspaceBranch = branchWorkspaces.length <= 1

  const isHidden =
    !isEditor ||
    branch === STANDARD_BRANCH ||
    ancestorsForViewBlock.length === 0

  const onTransfer = <T extends ConfigurationOperation>(
    props: BoundTransferProps<T>,
  ) => {
    if (selectedDestinationBlock) {
      return rawOnTransfer({
        ...props,
        sourceBlock: block,
        destinationBlock: selectedDestinationBlock,
        layerTopology,
      })
    }
    return Promise.resolve(undefined)
  }

  return {
    ancestorsForViewBlock,
    selectedDestinationBlock,
    isSingleOrNoWorkspaceBranch,
    currentWorkspaceKey,
    onTransfer,
    isHidden,
    isTransferModalOpen,
    setIsTransferModalOpen,
    setSelectedDestinationBlock,
  }
}
